import { DateBox, DateRangeBox, DropDownBox, List, RadioGroup, SelectBox } from 'devextreme-react';
import DataGrid, {
    Column,
    FilterRow,
    LoadPanel,
    Lookup,
    MasterDetail,
    Scrolling,
    Selection,
    Sorting,
    StateStoring,
    Summary,
    TotalItem
} from 'devextreme-react/data-grid';
import { Popup } from 'devextreme-react/popup';
import { TagBox } from 'devextreme-react/tag-box';
import { Tooltip } from 'devextreme-react/tooltip';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Col, Collapse, FormGroup, Label, Row, UncontrolledTooltip } from 'reactstrap';
import Breadcrumb from '../../../components/common/breadcrumb';
import api from '../../../services/api-configuration';
import { getAllBolge, getAllWarehouses } from '../../../services/apis';
import { getTaseron } from '../../../services/getTaseron';
import PopoverInfoIcon from '../../../utils/PopoverInfoIcon';
import useFormatMessage from '../../../utils/useFormatMessage';
import SevkEmriErrorIndicator from './SevkEmriErrorIndicator';
import TransferStatusIndicator from './TransferStatusIndicator';

// Utility functions
const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const sortData = (data) => {
    if (!Array.isArray(data)) return [];

    return [...data].sort((a, b) => {
        // First priority: BİLİNMEYEN comes first
        if (a.dealer_name === "BİLİNMEYEN" && b.dealer_name !== "BİLİNMEYEN") return -1;
        if (a.dealer_name !== "BİLİNMEYEN" && b.dealer_name === "BİLİNMEYEN") return 1;

        // Second priority: GROUPAGE_CANCELED comes next
        if (a.groupage_status === "GROUPAGE_CANCELED" && b.groupage_status !== "GROUPAGE_CANCELED") return -1;
        if (a.groupage_status !== "GROUPAGE_CANCELED" && b.groupage_status === "GROUPAGE_CANCELED") return 1;

        // Third priority: shipping_info (Nereye) alphabetically
        // Handle null values in shipping_info
        if (!a.shipping_info && !b.shipping_info) return 0;
        if (!a.shipping_info) return 1;
        if (!b.shipping_info) return -1;
        const compareShipping = a.shipping_info.localeCompare(b.shipping_info, 'tr-TR');
        if (compareShipping !== 0) return compareShipping;

        // Fourth priority: Priority (higher numbers first, null/undefined last)
        if (a.priority && !b.priority) return -1;
        if (!a.priority && b.priority) return 1;
        if (a.priority && b.priority) return Number(b.priority) - Number(a.priority);

        return 0;
    });
};
// Custom hook for API calls with optimized error handling
const useApiData = (apiCall, errorMessage, filterFn = null, dependencies = []) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await apiCall();
                if (!isMounted) return;

                let responseData = Array.isArray(response) ? response : response?.data?.data || [];
                if (filterFn) {
                    responseData = responseData.filter(filterFn);
                }
                setData(responseData);
                setError(null);
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
                setError(error);
                toast.error(errorMessage);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, dependencies);

    return [data, setData, isLoading, error];
};

const operationDescriptions = {
    between: "Arasında",
    contains: "İçerir",
    endswith: "İle biter",
    equal: "Eşittir",
    greaterThan: "Büyüktür",
    greaterThanOrEqual: "Büyük eşittir",
    lessThan: "Küçüktür",
    lessThanOrEqual: "Küçük eşittir",
    notContains: "İçermez",
    notEqual: "Eşit değildir",
    startsWith: "İle başlar",
    endsWith: "İle biter"
};

const GrupajHazirlama = () => {

    const sevkiyatDateRef = useRef()
    const kontratSahibiRef = useRef()
    const taseronFirmaRef = useRef()
    const peronRef = useRef()
    const modelRef = useRef()
    const nereden = useRef();
    const nereye = useRef();
    const { t } = useFormatMessage();
    const mainGridRef = useRef(null);
    const [filteredBolge, setFilteredBolge] = useState([]);
    const [filteredModel, setFilteredModel] = useState([]);

    // State management with custom hooks for API data
    const [warehouse] = useApiData(getAllWarehouses, 'Depo bilgileri getirilirken hata oluştu');
    const [bolge] = useApiData(getAllBolge, 'Bölge bilgileri getirilirken hata oluştu');
    const [brand] = useApiData(
        () => api.post("/getAllBrands"),
        'Marka bilgileri getirilirken hata oluştu'
    );
    const [groupageRegion] = useApiData(
        () => api.post("/shippingRegion/getAll"),
        'Bölge bilgileri getirilirken hata oluştu',
        item => item.is_active
    );
    const [dealer] = useApiData(
        () => api.post("/shippingLocation/getAll"),
        'Bayi bilgileri getirilirken hata oluştu',
        item => item.is_active
    );
    const [province] = useApiData(
        () => api.post("/city/getAll"),
        'İl bilgileri getirilirken hata oluştu',
        item => item.is_active
    );
    const [kontratSahibi] = useApiData(
        () => getTaseron({ contract_owner: true }),
        'Kontrat sahibi bilgileri getirilirken hata oluştu'
    );
    const [taseronFirma] = useApiData(
        () => getTaseron({ transport_company: true }),
        'Taşeron firma bilgileri getirilirken hata oluştu'
    );
    const [models] = useApiData(
        () => api.post("/getAllModels"),
        'Model bilgileri getirilirken hata oluştu'
    );

    // Main state management
    const [ilAndBayiCounts, setIlAndBayiCounts] = useState([]);
    const [data, setData] = useState([]);
    const [filterVisible, setFilterVisible] = useState(true);
    const [selectedData, setSelectedData] = useState([]);
    const [mainData, setMainData] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [minHeight, setMinHeight] = useState('calc(100vh - 200px)');
    const [isLoading, setIsLoading] = useState(false);

    // Popup related state
    const [popupVisible, setPopupVisible] = useState(false);
    const [groupageMode, setGroupageMode] = useState(null);
    const [selectedRoute, setSelectedRoute] = useState({
        nereden: null,
        nereye: null,
        peron: null
    });
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedContractor, setSelectedContractor] = useState(null);
    const [selectedSubContractor, setSelectedSubContractor] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isExpanded, setIsExpanded] = useState(true);
    const [peronn, setPeronn] = useState([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [pdiExitValue, setPdiExitValue] = useState(0);
    const [firstSelectedPeron, setFirstSelectedPeron] = useState(null);
    const [firstSelectedChassisNo, setFirstSelectedChassisNo] = useState(null);
    // Format loading_dock value
    const formatLoadingDock = useCallback((loadingDock) => {
        if (!loadingDock) return '';
        const parsedValue = parseInt(loadingDock);
        if (!isNaN(parsedValue)) {
            return `PP-${parsedValue}`;
        }
        return loadingDock;
    }, []);

    // Optimized getPeron function with debounce
    const getPeron = useCallback(_.debounce(async (value) => {
        if (!value) return;

        try {
            const response = await api.post("getPeronsByWarehouses", {
                warehouse_ids: [parseInt(value)]
            });
            const all_data = response.data.data || [];

            // Sort only by numeric part after "-"
            all_data.sort((a, b) => {
                const getNumericPart = (addressCode) => {
                    const parts = addressCode.split('-');
                    if (parts.length >= 2) {
                        const numericPart = parseInt(parts[1], 10);
                        return isNaN(numericPart) ? 0 : numericPart;
                    }
                    return 0;
                };

                const numA = getNumericPart(a.address_code);
                const numB = getNumericPart(b.address_code);

                return numA - numB;
            });

            setPeronn(all_data); // setPeronn olarak düzeltildi

            if (all_data?.length === 0) {
                toast.error("Peron bulunamadı. Lütfen seçili depo için peron ve adres tanımlayın.", {
                    autoClose: 10000
                });
            }

            // İlk seçilen şasenin peron bilgisini DropDownBox'a set et
            // Bu callback içinde firstSelectedPeron'a erişmek için closure kullanıyoruz
            // Aşağıdaki useEffect'te de kontrol edilecek
        } catch (error) {
            toast.error("Hata oluştu");
            console.error("Error fetching route:", error);
        }
    }, 300), []);

    // Handlers
    const handlePlatformChange = useCallback((e) => {
        setSelectedPlatform(e);
    }, []);

    const handleDateChange = useCallback((e) => {
        setSelectedDate(moment(e).format("YYYY-MM-DD"));
    }, []);

    const handleContractorChange = useCallback((e) => {
        setSelectedContractor(kontratSahibi.find(k => k.id === e)?.name || '');
    }, [kontratSahibi]);

    const handleSubContractorChange = useCallback((e) => {
        setSelectedSubContractor(taseronFirma.find(t => t.id === e)?.name || '');
    }, [taseronFirma]);

    const handlePopupVisibility = useCallback((mode) => {
        setGroupageMode(mode);
        setPopupVisible(true);
    }, []);

    const handleMainGridSelection = useCallback((e) => {
        const selectedRows = e.selectedRowsData;
        if (!selectedRows?.length) return;

        const scrollPosition = mainGridRef.current?.instance.getScrollable().scrollTop();

        if (selectedRows[0].client_code === "SY000002") {
            toast.error("Bu şaseyi seçebilmek için 'Sevk Yeri' tanımlanmalıdır.");
            return;
        }

        const selectedWarehouseName = selectedRows[0].warehouse_name;
        if (selectedWarehouse === null) {
            const warehouseId = warehouse.find(item => item.warehouse_name === selectedWarehouseName)?.id;
            setSelectedWarehouse(warehouseId);
            getPeron(warehouseId);
            setSelectedRoute({
                ...selectedRoute,
                nereden: selectedWarehouseName
            })
        }

        setSelectedData(prev => {
            // İlk seçilen şasenin peron bilgisini kaydet
            if (prev.length === 0) {
                const firstSelected = selectedRows[0];
                setFirstSelectedChassisNo(firstSelected.chassis_no);
                if (firstSelected.loading_dock) {
                    const parsedValue = parseInt(firstSelected.loading_dock);
                    if (!isNaN(parsedValue)) {
                        setFirstSelectedPeron(`PP-${parsedValue}`);
                    } else {
                        setFirstSelectedPeron(firstSelected.loading_dock);
                    }
                }
            }
            return [...prev, ...selectedRows];
        });
        setMainData(prev => prev.filter(
            item => !selectedRows.some(selected => selected.chassis_no === item.chassis_no)
        ));

        setTimeout(() => {
            if (mainGridRef.current) mainGridRef.current.instance.getScrollable().scrollTo(scrollPosition);
        }, 50);
    }, [selectedWarehouse, warehouse, getPeron, selectedRoute]);

    const handleSelectedGridSelection = useCallback((e) => {
        const selectedRows = e.selectedRowsData;
        setMainData(prev => [...prev, ...selectedRows]);
        setSelectedData(prev =>
            prev.filter(item => !selectedRows.some(selected => selected.chassis_no === item.chassis_no))
        );
    }, []);

    const handleSearch = useCallback(async () => {
        setSelectedData([]);
        setFirstSelectedPeron(null);
        setFirstSelectedChassisNo(null);
        setSelectedProvince(null);
        setIsLoading(true);

        const getSelectedValues = (elementId, valueKey = 'value') => {
            const element = document.getElementById(elementId);
            if (!element) return [];
            const nodes = element.childNodes[0]?.childNodes[0]?.childNodes;
            return Array.from(nodes || []).map(node => node[valueKey]);
        };

        const retValueForArr = (arr) => {
            if (arr.length === 0) return null;
            return arr;
        }
        const body = {
            city_ids: retValueForArr(getSelectedValues('province-select').map(Number)),
            client_codes: retValueForArr(getSelectedValues('dealer-select')),
            customs_clearance: true,
            region_ids: retValueForArr(getSelectedValues('groupage-region-select').map(Number)),
            warehouse_ids: retValueForArr(getSelectedValues('warehouse-select').map(Number)),
            zone_ids: retValueForArr(getSelectedValues('bolge-select').map(Number)),
            brands: retValueForArr(getSelectedValues('brand-select')),
            model_ids: document.getElementById("model-select").childNodes[0].childNodes[0].value === "" ? null : retValueForArr(document.getElementById("model-select").childNodes[0].childNodes[0].value.split(",").map(Number)),
            end_date: document.querySelector("#date-select").childNodes[2]?.childNodes[0]?.childNodes[0]?.value ?
                moment(document.querySelector("#date-select").childNodes[2].childNodes[0].childNodes[0].value).format("YYYY-MM-DD") : null,
            start_date: document.querySelector("#date-select").childNodes[0]?.childNodes[0]?.childNodes[0]?.value ?
                moment(document.querySelector("#date-select").childNodes[0].childNodes[0].childNodes[0].value).format("YYYY-MM-DD") : null,
            pdi_exit: document.getElementById("pdi-select").childNodes[0].value === "1" ? true : null,
        };

        try {
            const response = await api.post("/getEligibleVehicles", body);
            const vehicles = response.data.vehicles || [];

            // Sadece bir kere sıralama yap
            const availableVehicles = vehicles.filter(item => item.status === "AVAILABLE");
            const sortedVehicles = sortData(availableVehicles);

            // İl ve ilçe sayıları hesaplama
            const cityCounts = sortedVehicles.reduce((acc, curr) => {
                const cityKey = curr.city_name;
                const districtKey = curr.distrit_name;

                if (!acc[cityKey]) {
                    acc[cityKey] = {
                        il: cityKey,
                        count: 0,
                        districts: {},
                        hasPriority: false,
                        priorityValue: null
                    };
                }

                if (!acc[cityKey].districts[districtKey]) {
                    acc[cityKey].districts[districtKey] = {
                        district: districtKey,
                        count: 0,
                        priorityValue: null,
                        vehicles: []
                    };
                }

                acc[cityKey].districts[districtKey].vehicles.push(curr);

                if (curr.priority) {
                    acc[cityKey].hasPriority = true;
                    const currentPriority = Number(curr.priority);
                    if (!acc[cityKey].priorityValue || currentPriority > Number(acc[cityKey].priorityValue)) {
                        acc[cityKey].priorityValue = currentPriority;
                    }
                }

                const districtVehicles = acc[cityKey].districts[districtKey].vehicles;
                const maxPriority = Math.max(...districtVehicles.map(v => Number(v.priority) || 0));
                acc[cityKey].districts[districtKey].priorityValue = maxPriority || null;

                acc[cityKey].count += 1;
                acc[cityKey].districts[districtKey].count += 1;

                return acc;
            }, {});

            // ... önceki kod devamı ...

            const transformedCityCounts = Object.values(cityCounts).map(city => ({
                il: city.il,
                count: city.count,
                hasPriority: city.hasPriority,
                priorityValue: city.priorityValue,
                districts: Object.entries(city.districts).map(([districtName, districtData]) => ({
                    district: districtName,
                    count: districtData.count,
                    priorityValue: districtData.priorityValue,
                    il: city.il
                }))
            }));

            setIlAndBayiCounts(transformedCityCounts.sort((a, b) => {
                if (a.il === "BİLİNMEYEN" && b.il !== "BİLİNMEYEN") return -1;
                if (a.il !== "BİLİNMEYEN" && b.il === "BİLİNMEYEN") return 1;
                if (!a.il && !b.il) return 0;
                if (!a.il) return 1;
                if (!b.il) return -1;
                return a.il.localeCompare(b.il, 'tr-TR');
            }));
            setMainData(sortedVehicles); // Sıralanmış veriyi direkt set et
            setIsDataLoaded(true);
            setData(vehicles);

        } catch (error) {
            console.error("API Error:", error);
            toast.error("Veri yüklenirken hata oluştu");
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    const handleIlRowClick = useCallback((e) => {
        const clickedProvince = e.data.il;
        setSelectedProvince(prevProvince => prevProvince === clickedProvince ? null : clickedProvince);
        setSelectedDistrict(null); // Reset district selection when province changes
    }, []);

    const handleIlceClick = useCallback((district, province) => {
        // İlçe ve il birlikte kullanılarak benzersiz tanımlama
        const districtKey = `${district}_${province}`;
        setSelectedDistrict(prevDistrict => prevDistrict === districtKey ? null : districtKey);
        setSelectedProvince(province);
    }, []);

    const validatePP999 = (selectedData, peronn, selectedPeronId) => {
        // Check if all selected items are from the same warehouse
        const warehouses = [...new Set(selectedData.map(item => item.warehouse_name))];
        if (warehouses.length > 1) {
            // Multiple warehouses - must use PP-999
            const pp999Peron = peronn.find(p => p.address_code === "PP-999");

            if (!pp999Peron) {
                return {
                    isValid: false,
                    message: "Karışık Grupaj yapmak üzeresiniz. Seçtiğiniz diğer depolar için PP-999 peronu tanımşanmış olmalıdır.",
                    requiredAction: "CREATE_PP999"
                };
            }

            if (selectedPeronId !== pp999Peron.address_id) {
                return {
                    isValid: false,
                    message: "Farklı depolardan şase seçildiğinde PP-999 peronu kullanılmalıdır.",
                    requiredAction: "SELECT_PP999"
                };
            }
        }

        return {
            isValid: true,
            message: "",
            requiredAction: null
        };
    };


    // Validation
    const validationCheck = useCallback(() => {

        if (selectedData.length === 0) {
            toast.error(t("Seçili araç bulunmamaktadır") + " !");
            return false;
        }

        if (!selectedWarehouse) {
            toast.error("Nereden (Depo) seçiniz!");
            return false;
        }

        if (!selectedCity) {
            toast.error("Nereye (İl) seçiniz!");
            return false;
        }

        const selectedPeronId = document.getElementById("peron-select")?.childNodes[0]?.childNodes[0]?.value;
        if (!selectedPeronId) {
            toast.error(t("Peron seçiniz") + " !");
            return false;
        }
        // Validate PP-999 requirement
        // const pp999Validation = validatePP999(selectedData, peronn, Number(selectedPeronId));
        // if (!pp999Validation.isValid) {
        //     toast.warn(pp999Validation.message, { autoClose: 12000 });
        //    // return false;
        // }
        let selected = peronn.find(p => p.address_id === parseInt(selectedPeronId))
        let cp = selected?.capacity - (selected?.used_capacity + selected?.reserved_capacity);

        if (cp < selectedData?.length) {
            toast.error("Peron kapasitesi yetersiz");
            return false
        }

        const selectedPlatform = peronn.find(p => p.id === parseInt(selectedPeronId));
        if (selectedPlatform) {
            const availableCapacity = selectedPlatform.capacity - selectedPlatform.used_capacity;
            if (selectedData.length > availableCapacity) {
                toast.error(`Seçilen şase sayısı (${selectedData.length}) perondaki boş kapasiteden (${availableCapacity}) fazla olamaz!`, {
                    autoClose: 10000
                });
                return false;
            }
        }

        const plannedDate = document.getElementById("date-select-planned")?.childNodes[0]?.childNodes[0]?.value;
        if (!plannedDate) {
            toast.error(t("Planlanan Sevk Tarihini seçiniz") + " !");
            return false;
        }



        return true;
    }, [selectedData.length, selectedWarehouse, selectedCity, peronn, t]);

    const handleConfirm = useCallback(async (mode) => {
        const subContractorValue = document.getElementById("sub-kontrat-select")?.childNodes[0]?.childNodes[0]?.value;
        const body = {
            address_id: parseInt(document.getElementById("peron-select").childNodes[0].childNodes[0].value),
            chassis_numbers: _.uniq(selectedData.map(item => item.chassis_no)),
            contractor_id: parseInt(document.getElementById("kontrat-select").childNodes[0].childNodes[0].value),
            subcontractor_id: subContractorValue ? parseInt(subContractorValue) : null,
            groupage_type: mode === 'bulk' ? "MULTIPLE" : "SINGLE",
            route_id: 2,
            shipping_date: selectedDate,
            warehouse_id: selectedWarehouse,
            city_id: selectedCity,
            shipping_type_id: 2
        };
        try {
            await api.post("/createGroupage", body);
            toast.success('Grupaj işlemi başarıyla tamamlandı');
            setTimeout(() => {
                setPopupVisible(false);
                setSelectedData([]);
                setFirstSelectedPeron(null);
                setFirstSelectedChassisNo(null);
                if (sevkiyatDateRef.current) sevkiyatDateRef.current.instance.reset();
                if (kontratSahibiRef.current) kontratSahibiRef.current.instance.reset();
                if (taseronFirmaRef.current) taseronFirmaRef.current.instance.reset();
                if (peronRef.current) peronRef.current.instance.reset();
                if (nereden.current) nereden.current.instance.reset();
                if (nereye.current) nereye.current.instance.reset();

                setSelectedWarehouse(null);
                setSelectedCity(null);
                setSelectedDate(null);
                setSelectedContractor(null);
                setSelectedSubContractor(null);
                setSelectedPlatform(null);
                setPeronn([]);
            }, 300);


        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Hata oluştu");
        }
    }, [selectedData, selectedDate, selectedWarehouse, selectedCity]);

    // Cell styling
    const onCellPrepared = useCallback((e) => {
        if (e.rowType !== "data") return;

        if (e.data.client_code === "SY000002") {
            e.cellElement.style.background = "#ff899e";
        }

        if (e.column.dataField === "priority") {
            const priorityValue = Number(e.text);
            if (!priorityValue) return;

            if (priorityValue === 99) {
                e.cellElement.style.background = "red";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "bold";
            } else if ([3, 4, 5].includes(priorityValue)) {
                e.cellElement.style.background = "#ff4263";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "bold";
            } else {
                e.cellElement.style.background = "pink";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "bold";
            }
        }

        if (e.column.dataField === "pdi_order" && e.text === "1" ||
            e.column.dataField === "pdi_status" && e.text === "1") {
            e.cellElement.style.background = "#00b500";
            e.cellElement.style.color = "white";
            e.cellElement.style.fontWeight = "bold";
        }

        if (
            ((e.column.dataField === "groupage_status" && e.text === "1")
                ||
                (e.data.groupage_status !== "NO_GROUPAGE" && e.column.dataField === "groupage_status")) &&
            e.data.groupage_status !== "GROUPAGE_COMPLETED"
        ) {
            e.cellElement.style.background = "red";
            e.cellElement.style.color = "white";
            e.cellElement.style.fontWeight = "bold";
        }
    }, []);

    // Master Detail Component
    const BayiMasterDetail = useCallback(({ data }) => {
        return (
            <div className="p-2">
                <table className="table table-hover table-bordered mb-0" style={{ fontSize: '0.9rem' }}>
                    <tbody>
                        {data.data.districts.map((district, index) => {
                            const priorityValue = Number(district.priorityValue);
                            const isHighPriority = [99, 5, 3, 4].includes(priorityValue);
                            const districtKey = `${district.district}_${district.il}`;
                            const isSelected = selectedDistrict === districtKey;

                            return (
                                <tr
                                    key={`${district.district}_${district.il}_${index}`}
                                    onClick={() => handleIlceClick(district.district, district.il)}
                                    style={{
                                        cursor: 'pointer',
                                        background: isHighPriority ? 'rgb(254 188 199)' : 'white',
                                        fontWeight: isSelected ? 'bold' : 'normal',
                                        color: isSelected ? '#007bff' : 'inherit'
                                    }}
                                >
                                    <td style={{
                                        borderColor: '#dee2e6',
                                        padding: '0.5rem 0.75rem',
                                        width: '70%'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>
                                                {district.district}
                                                {isHighPriority && (
                                                    <span style={{ color: '#ed0000', marginLeft: '4px' }}>
                                                        ({priorityValue})
                                                    </span>
                                                )}
                                            </span>
                                            {/* <small style={{ color: '#666', fontSize: '0.8em' }}>
                                                {district.il}
                                            </small> */}
                                        </div>
                                    </td>
                                    <td style={{
                                        borderColor: '#dee2e6',
                                        textAlign: 'center',
                                        padding: '0.5rem',
                                        width: '30%'
                                    }}>
                                        {district.count}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }, [selectedDistrict, handleIlceClick]);

    // Warehouse and City Selection Component
    const WarehouseCitySelection = useCallback(({
        onWarehouseChange,
        onCityChange
    }) => {
        return (
            <>
                <FormGroup style={{ marginBottom: 0 }}>
                    <Label style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>Nereden</Label>
                    <SelectBox
                        ref={nereden}
                        id="departure-warehouse-select"
                        placeholder="Depo Seçiniz"
                        showClearButton={true}
                        value={selectedWarehouse}
                        dataSource={warehouse}
                        displayExpr="warehouse_name"
                        valueExpr="id"
                        searchEnabled={true}
                        onValueChange={onWarehouseChange}
                    />
                </FormGroup>

                <FormGroup style={{ marginBottom: 0 }}>
                    <Label style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>Nereye</Label>
                    <SelectBox
                        ref={nereye}
                        id="destination-city-select"
                        placeholder="İl Seçiniz"
                        value={selectedCity}
                        showClearButton={true}
                        dataSource={province?.filter(item => item.id !== 82)}
                        displayExpr="city_name"
                        valueExpr="id"
                        searchEnabled={true}
                        onValueChange={onCityChange}
                    />
                </FormGroup>
            </>
        );
    }, [warehouse, province, selectedWarehouse, selectedCity]);

    // Confirmation Popup Component
    const GroupageConfirmationPopup = useCallback(({
        visible,
        onHiding,
        onConfirm,
        mode,
        selectedData,
        route,
        platform,
        plannedDate,
        contractor
    }) => {
        if (!visible) return null;
        const handleConfirmClick = async () => {
            setIsConfirming(true);
            // her şeyi nulla

            // setSelectedDate(null);
            // setSelectedContractor(null);
            // setSelectedWarehouse(null);
            // setSelectedCity(null);
            // setSelectedData([]);
            // setPeronn([]);

            onHiding();


            try {
                await onConfirm(mode);
                onHiding();
            } catch (error) {
                toast.error(error.response?.data?.message || 'Grupaj işlemi başarısız oldu');
            } finally {
                setTimeout(() => {
                    setIsConfirming(false);
                    getPeron();
                }, 1000);
            }
        };

        const renderChassisGrids = () => {
            if (mode === 'individual') {
                return selectedData.map((item, index) => (
                    <div key={index} className="mb-4">
                        <DataGrid
                            className='data-grid-header-gray data-grid-gray'
                            dataSource={[item]}
                            showBorders={true}
                            showRowLines={true}
                            showColumnLines={true}
                        >
                            <Column alignment='center' dataField="chassis_no" caption="Şase" />
                            <Column alignment='center' caption='Model' dataField='model'

                            />
                            <Column alignment='center' dataField="dealer_name" caption="Bayi" />
                            <Column alignment='center' dataField="city_name" caption="İl" />
                        </DataGrid>
                    </div>
                ));
            }

            return (
                <DataGrid
                    className='data-grid-header-gray data-grid-gray'
                    dataSource={selectedData}
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={true}
                >
                    <Column alignment='center' dataField="chassis_no" caption="Şase" />
                    <Column alignment='center' caption='Model' dataField='model'

                    />
                    <Column alignment='center' dataField="dealer_name" caption="Bayi" />
                    <Column alignment='center' dataField="city_name" caption="İl" />
                </DataGrid>
            );
        };

        return (
            <Popup
                visible={visible}
                onHiding={onHiding}
                dragEnabled={false}
                closeOnOutsideClick={true}
                showTitle={true}
                title={mode === 'bulk' ? 'Toplu Grupaj Onayı' : 'Tekli Grupaj Onayı'}
                width={800}
                height={600}
            >
                <div className="p-2">
                    <div className="mb-4">
                        <table className="table table-bordered mt-2">
                            <tbody>
                                <tr>
                                    <td style={{ borderColor: "#ddd" }} width="25%"><strong>Rota:</strong></td>
                                    <td style={{ borderColor: "#ddd" }} width="25%">
                                        {route.nereden} - {route.nereye}
                                    </td>
                                    <td style={{ borderColor: "#ddd" }} width="25%"><strong>Peron:</strong></td>
                                    <td style={{ borderColor: "#ddd" }} width="25%">{
                                        route.peron || ""
                                    }</td>
                                </tr>
                                <tr>
                                    <td style={{ borderColor: "#ddd" }}><strong>Planlanan Sevk:</strong></td>
                                    <td style={{ borderColor: "#ddd" }}>{formatDate(plannedDate)}</td>
                                    <td style={{ borderColor: "#ddd" }}><strong>Kontrat Sahibi:</strong></td>
                                    <td style={{ borderColor: "#ddd" }}>{contractor}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mb-4">
                        <hr />
                        {renderChassisGrids()}
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <Button
                            color="secondary"
                            onClick={onHiding}
                            className="mr-2"
                            disabled={isConfirming}
                        >
                            İptal
                        </Button>
                        <Button
                            color={mode === 'bulk' ? 'danger' : 'warning'}
                            onClick={handleConfirmClick}
                            disabled={isConfirming}
                        >
                            {isConfirming ? 'İşlem yapılıyor...' : 'Onayla'}
                        </Button>
                    </div>
                </div>
            </Popup>
        );
    }, [isConfirming, getPeron]);

    // Effects
    useEffect(() => {

        localStorage.setItem("storage", "");

        if (document.querySelector(".page-main-header")) document.querySelector(".page-main-header").classList.add('open');
        if (document.querySelector(".page-sidebar")) document.querySelector(".page-sidebar").classList.add('open');

        const calculateHeight = () => {
            const headerHeight = document.querySelector('.page-main-header')?.offsetHeight || 0;
            const breadcrumbHeight = document.querySelector('.breadcrumb')?.offsetHeight || 0;
            const filterCardHeight = document.querySelector('.card')?.offsetHeight || 0;
            const padding = 48;
            setMinHeight(`calc(100vh - ${headerHeight + breadcrumbHeight + filterCardHeight + padding}px)`);
        };

        calculateHeight();
        window.addEventListener('resize', calculateHeight);
        return () => window.removeEventListener('resize', calculateHeight);
    }, []);

    useEffect(() => {
        if (selectedData?.length === 0) {
            setSelectedWarehouse(null);
            setFirstSelectedPeron(null);
            setFirstSelectedChassisNo(null);
        }
    }, [selectedData]);

    // İlk seçilen şasenin peron bilgisini DropDownBox'a otomatik seç
    useEffect(() => {
        if (firstSelectedPeron && peronn.length > 0 && peronRef.current?.instance) {
            const matchingPeron = peronn.find(p => p.address_code === firstSelectedPeron);
            if (matchingPeron) {
                // DropDownBox'ın value'sunu set et
                const currentValue = peronRef.current.instance.option("value");
                // Eğer zaten seçili değilse set et
                if (currentValue !== matchingPeron.address_id) {
                    peronRef.current.instance.option("value", matchingPeron.address_id);
                    setSelectedPlatform(matchingPeron.address_id);
                    setSelectedRoute(prev => ({
                        ...prev,
                        peron: matchingPeron.address_code
                    }));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstSelectedPeron, peronn]);

    useEffect(() => {
        if (!isDataLoaded || !data.length) return;

        const availableData = data.filter(item => item.status === "AVAILABLE");
        let filteredData = availableData;

        if (selectedProvince) {
            filteredData = filteredData.filter(item => item.city_name === selectedProvince);
        }

        if (selectedDistrict) {
            // İlçe ve il birlikte kontrol ediliyor
            const [districtName, provinceName] = selectedDistrict.split('_');
            filteredData = filteredData.filter(item =>
                item.distrit_name === districtName &&
                item.city_name === provinceName
            );
        }

        setMainData(sortData(filteredData));
    }, [selectedProvince, selectedDistrict, isDataLoaded, data]);

    // Custom filter operations
    const customFilterOperations = {
        contains: (filterValue, value) => {
            if (!filterValue || !value) return false;
            return value.toString()
                .toLocaleLowerCase('tr-TR')
                .indexOf(filterValue.toString().toLocaleLowerCase('tr-TR')) !== -1;
        },
        '=': (filterValue, value) => {
            if (!filterValue || !value) return false;
            return value.toString()
                .localeLowerCase('tr-TR') ===
                filterValue.toString().localeLowerCase('tr-TR');
        },
        startswith: (filterValue, value) => {
            if (!filterValue || !value) return false;
            return value.toString()
                .localeLowerCase('tr-TR')
                .startsWith(filterValue.toString().localeLowerCase('tr-TR'));
        },
        endswith: (filterValue, value) => {
            if (!filterValue || !value) return false;
            return value.toString()
                .localeLowerCase('tr-TR')
                .endsWith(filterValue.toString().localeLowerCase('tr-TR'));
        }
    };

    return (
        <>
            <Breadcrumb current={"Yurt İçi Sevkiyat Grupaj Hazırlama"} />
            <div className="p-3">
                <Card className="mb-3 pb-2">
                    <div className="p-1">
                        {filterVisible ?
                            <i
                                style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    color: "#3b3b3b"
                                }}
                                className="fa fa-chevron-up float-right cursor-pointer p-1"
                                onClick={() => setFilterVisible(false)}
                            /> :
                            <i
                                style={{
                                    cursor: "pointer",
                                    fontSize: "1.1rem",
                                    color: "#3b3b3b"
                                }}
                                className="fa fa-chevron-down float-right cursor-pointer p-1"
                                onClick={() => setFilterVisible(true)}
                            />
                        }

                        <Collapse isOpen={filterVisible}>
                            <Row>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Depo</Label>
                                        <TagBox
                                            placeholder="Depo Seçiniz"
                                            showClearButton={true}
                                            dataSource={warehouse}
                                            displayExpr="warehouse_name"
                                            valueExpr="id"
                                            maxDisplayedTags={3}
                                            id='warehouse-select'
                                            searchEnabled={true}
                                            multiline={true}
                                            showSelectionControls={true}
                                            onValueChange={(e) => {
                                                setSelectedWarehouseIds(e || []);
                                                if (e.length === 0) {
                                                    setFilteredBolge([]);
                                                }
                                                else {
                                                    setFilteredBolge(bolge.filter(b =>
                                                        e.includes(b.warehouse_id)
                                                    ));
                                                }
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Bölge</Label>
                                        <TagBox
                                            placeholder="Bölge Seçiniz"
                                            showClearButton={true}
                                            dataSource={filteredBolge}
                                            displayExpr={(e) => e?.zone_name + ` (${e?.warehouse_name})`}
                                            valueExpr="id"
                                            maxDisplayedTags={1}
                                            id='bolge-select'
                                            searchEnabled={true}
                                            multiline={true}
                                            showSelectionControls={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Grupaj Bölgesi</Label>
                                        <TagBox
                                            placeholder="Sevk Bölgesi Seçebilirsiniz"
                                            dataSource={groupageRegion}
                                            valueExpr="id"
                                            displayExpr="name"
                                            id='groupage-region-select'
                                            showClearButton={true}
                                            maxDisplayedTags={3}
                                            searchEnabled={true}
                                            multiline={true}
                                            showSelectionControls={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Bayi</Label>
                                        <TagBox
                                            placeholder="Bayi Seçebilirsiniz"
                                            showClearButton={true}
                                            dataSource={dealer}
                                            id='dealer-select'
                                            valueExpr="code"
                                            displayExpr={(e) => e?.group_company_title + ` (${e?.brand})`}
                                            maxDisplayedTags={3}
                                            searchEnabled={true}
                                            multiline={true}
                                            showSelectionControls={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Sevkiyat İli</Label>
                                        <TagBox
                                            placeholder="Sevk İli Seçebilirsiniz"
                                            showClearButton={true}
                                            dataSource={
                                                province?.sort((a, b) => {
                                                    if (a.city_name === "BİLİNMEYEN" && b.city_name !== "BİLİNMEYEN") return -1;
                                                    if (a.city_name !== "BİLİNMEYEN" && b.city_name === "BİLİNMEYEN") return 1;
                                                    if (!a.city_name && !b.city_name) return 0;
                                                    if (!a.city_name) return 1;
                                                    if (!b.city_name) return -1;
                                                    return a.city_name.localeCompare(b.city_name, 'tr-TR');
                                                })
                                            }
                                            id='province-select'
                                            valueExpr="id"
                                            displayExpr="city_name"
                                            maxDisplayedTags={3}
                                            searchEnabled={true}
                                            multiline={true}
                                            showSelectionControls={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>Marka</Label>
                                        <TagBox
                                            id='brand-select'
                                            placeholder="Marka Seçebilirsiniz"
                                            showClearButton={true}
                                            dataSource={brand}
                                            displayExpr="brand_name"
                                            valueExpr="brand_code"
                                            maxDisplayedTags={3}
                                            searchEnabled={true}
                                            onValueChange={(e) => {
                                                setSelectedBrands(e || []);
                                                if (e.length === 0) {
                                                    setFilteredModel([]);
                                                }
                                                else {
                                                    let brand_id = brand.filter(b => e.includes(b.brand_code)).map(b => b.id)
                                                    setFilteredModel(models.filter(m =>
                                                        brand_id.includes(m.brand_id)
                                                    ));
                                                }

                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3" className='p-2'>
                                    <FormGroup>
                                        <Label>Model</Label>
                                        <DropDownBox
                                            id='model-select'
                                            placeholder="Model Seçebilirsiniz"
                                            dataSource={filteredModel}
                                            displayExpr={(e) => e?.model_name + ` (${e?.brand_name})`}
                                            valueExpr="id"
                                            searchEnabled={true}
                                            showClearButton={true}
                                            onValueChanged={(e) => {
                                                if (e.value === "" || e.value === null) {
                                                    if (modelRef.current) {
                                                        modelRef.current.instance.clearSelection();
                                                    }
                                                }
                                            }}

                                            contentRender={(comp) => {
                                                return (
                                                    <DataGrid
                                                        dataSource={filteredModel}
                                                        ref={modelRef}
                                                        showBorders={true}
                                                        showRowLines={true}
                                                        showColumnLines={true}
                                                        hoverStateEnabled={true}
                                                        height={"300"}
                                                        key={"id"}
                                                        keyExpr={"id"}
                                                        allowColumnResizing={true}
                                                        onSelectionChanged={(e) => {
                                                            comp.component.option("value", e.selectedRowKeys);
                                                        }}
                                                        grouping={{
                                                            autoExpandAll: true,
                                                        }}
                                                        scrolling={{
                                                            mode: "infinite"
                                                        }}
                                                        paging={{
                                                            enabled: false,
                                                        }}
                                                    >
                                                        <FilterRow visible={true} />
                                                        <Selection mode="multiple"
                                                            showCheckBoxesMode='always'
                                                            selectAllMode='page'
                                                            allowSelectAll={true}

                                                        />
                                                        <Column dataField="brand_name" caption="Marka"
                                                            groupIndex={0}
                                                        />
                                                        <Column dataField="model_name" caption="Model" />
                                                        <Column dataField="model_code" caption="Kod" width={"65"} />

                                                    </DataGrid>
                                                );
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="2" className='p-2'>
                                    <FormGroup>
                                        <Label>{"Fatura Trh"}</Label>
                                        <DateRangeBox
                                            id='date-select'
                                            displayFormat="dd/MM/yyyy"
                                            showClearButton
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md="3" className='p-2'>
                                    <FormGroup>
                                        <Label className='w-100 d-flex align-items-center'>
                                            PDI Durumu
                                        </Label>
                                        <RadioGroup
                                            id='pdi-select'
                                            items={[
                                                { value: 1, text: 'Yapılanlar' },
                                                { value: 0, text: 'Hepsi' },
                                            ]}
                                            valueExpr="value"
                                            displayExpr="text"
                                            defaultValue={0}
                                            layout="horizontal"
                                            onValueChanged={(e) => {
                                                setPdiExitValue(e.value);
                                            }}
                                        />
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginTop: '8px',
                                            flexWrap: 'wrap'
                                        }}>

                                        </div>
                                    </FormGroup>
                                </Col>
                                <Col md="3" className='p-2 d-flex align-items-center' style={{ gap: "10px" }}>
                                    <TransferStatusIndicator />
                                    &emsp;
                                    <SevkEmriErrorIndicator />
                                </Col>
                            </Row>

                            <Row className='justify-content-end'>
                                <Col md="11" className='d-flex justify-content-end mr-2'>
                                    {/* Tümünü seç */}
                                    <Button
                                        color='primary'
                                        onClick={() => {
                                            if (mainGridRef.current) mainGridRef.current.instance.selectAll();
                                        }}>
                                        <i className="fa fa-check-square"></i> Tümünü Seç
                                    </Button>
                                    &emsp;
                                    {/* Tümünü temizle */}
                                    <Button
                                        onClick={() => {
                                            if (mainGridRef.current) mainGridRef.current.instance.clearSorting();
                                        }}
                                    >
                                        <i className="fa fa-sort"></i> Temizle
                                    </Button>
                                    &emsp;
                                    <Button color="success" onClick={handleSearch} className='mr-1'
                                        disabled={brand?.length === 0}>
                                        <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-list'}`}></i> &nbsp;
                                        <span>Listele</span>
                                    </Button>
                                </Col>
                            </Row>
                        </Collapse>
                    </div>
                </Card>

                <Row className='main-content'>
                    <Col md={isExpanded ? "2" : "0"} className="px-0">
                        <DataGrid
                            dataSource={ilAndBayiCounts}
                            showBorders={true}
                            height="100%"
                            showColumnLines={true}
                            showRowLines={true}
                            hoverStateEnabled={true}
                            onCellPrepared={(e) => {
                                if (e.rowType === "data" && e.data.hasPriority) {
                                    if ([99, 5, 3, 4].includes(e.data.priorityValue)) {
                                        e.cellElement.style.background = "rgb(254 188 199)";
                                        e.cellElement.style.fontWeight = "bold";
                                    }
                                }
                            }}
                        >
                            <StateStoring
                                enabled={true}
                                type="localStorage"
                                savingTimeout={100}

                            />
                            <LoadPanel enabled={false} />
                            <MasterDetail
                                enabled={true}
                                component={BayiMasterDetail}
                            />
                            <FilterRow
                                visible={true}
                                operationDescriptions={operationDescriptions}
                            />
                            <Column
                                dataField="il"
                                caption="İl Adı"
                                cellRender={(cellData) => {
                                    return (
                                        <div
                                            onClick={() => handleIlRowClick(cellData)}
                                            style={{
                                                cursor: 'pointer',
                                                fontWeight: selectedProvince === cellData.value ? 'bold' : 'normal',
                                                color: selectedProvince === cellData.value ? '#007bff' : 'inherit'
                                            }}
                                        >
                                            {cellData.value} &nbsp;
                                            {[99, 5, 3, 4].includes(cellData.data.priorityValue) ? <span
                                                style={{
                                                    color: '#ed0000',
                                                    fontSize: '0.8rem',
                                                }}
                                            >{cellData.data.priorityValue}</span> : ""}
                                        </div>
                                    )
                                }}
                            />
                            <Column
                                dataField="count"
                                caption="Adet"
                                width="80"
                                alignment='center'
                            />
                            <Summary>
                                <TotalItem
                                    column="count"
                                    summaryType="sum"
                                    customizeText={(e) => `Top: ${e.value}`}
                                />
                            </Summary>
                        </DataGrid>
                    </Col>

                    <Col
                        md={isExpanded ? "10" : "12"}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.8rem",
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <div className="card p-2 m-0">
                            <DataGrid
                                dataSource={mainData}
                                showBorders={true}
                                height="42vh"
                                ref={mainGridRef}
                                onSelectionChanged={handleMainGridSelection}
                                showColumnLines={true}
                                showRowLines={true}
                                hoverStateEnabled={true}
                                style={{ minWidth: "100%" }}
                                onCellPrepared={onCellPrepared}
                                className='data-grid-gray'
                                allowColumnResizing={true}
                                keyExpr="chassis_no"
                            >
                                <StateStoring
                                    enabled={true}
                                    type="localStorage"
                                    savingTimeout={100}
                                />
                                <Sorting mode="multiple" />
                                <LoadPanel enabled={false} />
                                <Scrolling
                                    mode="virtual"
                                    rowRenderingMode="virtual"
                                    // useNative={true}
                                    preloadEnabled={true}
                                />
                                <Selection mode="multiple" showCheckBoxesMode="none" />
                                <FilterRow
                                    visible={true}
                                    operationDescriptions={operationDescriptions}
                                />

                                <Column
                                    alignment='center'
                                    caption='Gün'
                                    dataField='KalanGun'
                                    width={60}
                                    calculateDisplayValue={(e) => {
                                        return String(parseInt(moment().diff(e.process_date, 'days')) * -1)
                                    }}
                                    calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                                        return [
                                            [(data) => {
                                                const displayValue = String(parseInt(moment().diff(data.process_date, 'days')) * -1);
                                                return displayValue.toLowerCase().includes(filterValue.toLowerCase());
                                            }]
                                        ];
                                    }}
                                />
                                <Column
                                    alignment='center'
                                    dataField="process_date"
                                    caption="FaturaTarih"
                                    dataType="date"
                                    width={108}
                                    format="dd/MM/yyyy"
                                />
                                <Column
                                    alignment='center'
                                    caption='Öncelik'
                                    dataField='priority'
                                    width={55}
                                />
                                <Column
                                    alignment='center'
                                    dataField="chassis_no"
                                    caption="Şase"
                                    width={170}
                                />
                                <Column caption='Model'
                                    dataField='model'
                                    width={"95"}

                                />
                                <Column
                                    alignment='center'
                                    dataField="vehicle_type"
                                    caption="Tip"
                                    width={110}
                                    calculateDisplayValue={(e) => t(e.vehicle_type)}
                                >
                                    <Lookup dataSource={[
                                        { value: "PASSENGER", text: "Binek" },
                                        { value: "COMMERCIAL", text: "Ticari" },
                                        { value: "HEAVY_COMMERCIAL", text: "Ağır Ticari" },
                                    ]} valueExpr="value" displayExpr="text" />
                                </Column>
                                <Column
                                    alignment='center'
                                    dataField="warehouse_name"
                                    caption="Nereden"
                                    minWidth={120}
                                />
                                <Column
                                    alignment='center'
                                    dataField="shipping_info"
                                    caption="Nereye"
                                    minWidth={130}
                                />
                                <Column
                                    alignment='center'
                                    caption='Blg. Trh'
                                    dataField='blg_date'
                                    dataType='date'
                                    width={93}
                                    format="dd/MM/yyyy"
                                />
                                <Column
                                    alignment='center'
                                    caption='Park Adress'
                                    dataField='park_address'
                                    width={115}
                                />
                                <Column
                                    caption='Grp. Bz.'
                                    dataField='groupage_status'
                                    width={45}
                                    alignment='center'
                                    cssClass='datagrid-column-padding-2'
                                    headerCellRender={(e) => {
                                        return (
                                            <span>
                                                Grp.
                                                <br />
                                                Bz.
                                            </span>
                                        )
                                    }}
                                    calculateDisplayValue={(e) => {
                                        return e.groupage_status === "GROUPAGE_CANCELED" ? "1" : "0"
                                    }}
                                    calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                                        return [[(data) => {
                                            const displayValue = data.groupage_status === "GROUPAGE_CANCELED" ? "1" : "0";
                                            return displayValue === filterValue;
                                        }]];
                                    }}>
                                </Column>
                                <Column
                                    alignment='center'
                                    caption='PDI Emr.'
                                    dataField='pdi_order'
                                    width={50}
                                    cssClass='datagrid-column-padding-2'
                                    calculateDisplayValue={(e) => {
                                        return e.pdi_order === "YENİ" ? "1" : "0"
                                    }}
                                    calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                                        return [
                                            [(data) => {
                                                const displayValue = data.pdi_order === "YENİ" ? "1" : "0";
                                                return displayValue === filterValue;
                                            }]
                                        ];
                                    }}
                                    headerCellRender={(e) => {
                                        return (
                                            <span>
                                                PDI
                                                <br />
                                                Emr.
                                            </span>
                                        )
                                    }}
                                />
                                <Column
                                    alignment='center'
                                    caption='PDI'
                                    dataField='pdi_status'
                                    width={42}
                                    calculateDisplayValue={(e) => {
                                        return e.pdi_status === "1" ? "1" : "0"
                                    }}
                                />
                            </DataGrid>
                        </div>

                        {/* Bottom Section */}
                        <div className="card p-2 m-0">
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 2fr 1fr 1fr 1fr auto",
                                    gap: "0.5rem",
                                    alignItems: "end",
                                    zoom: "0.9"
                                }}
                            >
                                <WarehouseCitySelection
                                    onWarehouseChange={(value) => {
                                        getPeron(value);
                                        setSelectedWarehouse(value);
                                        setSelectedRoute({
                                            ...selectedRoute,
                                            nereden: warehouse.find(w => w.id === value)?.warehouse_name

                                        })

                                    }}
                                    onCityChange={(value) => {
                                        setSelectedCity(value);
                                        setSelectedRoute({
                                            ...selectedRoute,
                                            nereye: province.find(p => p.id === value)?.city_name
                                        })
                                    }}
                                />

                                <FormGroup style={{ marginBottom: 0 }}>
                                    <Label className='d-flex align-items-end' style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}
                                    >Peron &nbsp;
                                        <PopoverInfoIcon
                                            index={1}
                                            textInfoBreak={`
                                            Karışık depolardan grupaj yaparken PP-999 peronu seçilmelidir. 'br' Seçili depolarda PP-999 adresi tanımlanmamışsa lütfen tanımlayınız.
                                            'br'
                                            R: Rezerve Kapasite,'br' K: Kullanılan Kapasite,'br' T: Toplam Kapasite
                                        `} text={""} />
                                    </Label>
                                    {/* <SelectBox
                                        id='peron-select'
                                        placeholder="Peron Seçebilirsiniz"
                                        showClearButton={true}
                                        dataSource={peronn}
                                        ref={peronRef}
                                        searchEnabled={true}
                                        displayExpr={(e) => {
                                            if (e) {
                                                return `${e.warehouse_name} | ${e.address_code} | ${t(e.status)} | (${e.used_capacity}/${e.capacity})`
                                            }
                                        }}
                                        valueExpr="id"
                                        onValueChange={(e) => {
                                            handlePlatformChange(e);
                                            setSelectedRoute({
                                                ...selectedRoute,
                                                peron: peronn.find(p => p.id === e)?.address_code
                                            })
                                        }}
                                    /> */}
                                    <DropDownBox
                                        id='peron-select'
                                        ref={peronRef}
                                        dataSource={peronn}
                                        valueExpr={"address_id"}
                                        displayExpr={(e) => {
                                            if (e) {
                                                return `${e.zone_name} | ${e.address_code} | (${e.used_capacity + e.reserved_capacity}/${e.capacity})`
                                            }
                                        }}
                                        contentRender={(comp) => {
                                            return (
                                                <List
                                                    pageLoadMode="scrollBottom"
                                                    searchEnabled={true}
                                                    dataSource={peronn}
                                                    displayExpr={(item) => (`${item?.zone_name} | ${item?.address_code} | ${item?.reserved_capacity}R / ${item?.used_capacity}K / ${item?.capacity}` + "T")}
                                                    searchExpr={['address_code']}
                                                    style={{
                                                        minHeight: "250px",
                                                    }}
                                                    id='peron-item-list'

                                                    itemRender={(item) => {
                                                        return (
                                                            <div className="peron-select-grupaj"
                                                                onClick={() => {
                                                                    //selectedDataların hepsinin warehouse_name'i aynı mı?
                                                                    let isMulti = selectedData.every((item) => item.warehouse_name === selectedData[0].warehouse_name);
                                                                    console.log(isMulti);
                                                                    if (isMulti === false) {
                                                                        // PP-999 seçmeli yoksa uyarı vermeli PP-999 tanımlayın diye
                                                                        let peron = peronn.find(p => p.address_code === "PP-999");
                                                                        if (peron) {
                                                                            comp.component.option("value", peron.address_id); // Changed to address_code
                                                                            peronRef.current.instance.option("value", peron.address_id); // Changed to id
                                                                            handlePlatformChange(peron.id);
                                                                            setSelectedRoute({
                                                                                ...selectedRoute,
                                                                                peron: peron.address_code
                                                                            });
                                                                        }
                                                                        else {
                                                                            toast.error("Karışık depolardan grupaj yaparken PP-999 peronu seçilmelidir. Seçili depoda PP-999 adresi tanımlanmamış. Lütfen tanımlayınız.", {
                                                                                autoClose: 12000
                                                                            });
                                                                            return
                                                                        }
                                                                    }
                                                                    comp.component.option("value", item.address_code); // Changed to address_code
                                                                    peronRef.current.instance.option("value", item.address_id); // Changed to id
                                                                    handlePlatformChange(item.address_id);
                                                                    setSelectedRoute({
                                                                        ...selectedRoute,
                                                                        peron: item.address_code
                                                                    });
                                                                    comp.component.close(); // Add this to close dropdown
                                                                }}
                                                                style={{
                                                                    padding: "10px",
                                                                    color: "black",
                                                                    border: "none",
                                                                    // borderBottom: "1px solid #5f5f5f",


                                                                    backgroundColor: (item.used_capacity + item.reserved_capacity) >= item.capacity ? "#ffa7a7" :
                                                                        (item.used_capacity + item.reserved_capacity) > 0 ? "#ffd486" : "white"
                                                                }}>
                                                                <div>
                                                                    {item?.zone_name} | {item?.address_code} | {item?.reserved_capacity}R | ({item?.reserved_capacity + item?.used_capacity} / {item?.capacity})
                                                                </div>
                                                            </div>
                                                        )
                                                    }}
                                                />
                                            );
                                        }}
                                        placeholder="Peron Seçebilirsiniz"
                                    />
                                </FormGroup>

                                <FormGroup style={{ marginBottom: 0 }}>
                                    <Label style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>Planlanan Sevk Trh</Label>
                                    <DateBox
                                        id='date-select-planned'
                                        ref={sevkiyatDateRef}
                                        displayFormat="dd/MM/yyyy"
                                        type="date"
                                        // geçmiş tarihleri kapat
                                        min={new Date()}
                                        onValueChange={handleDateChange}
                                        width="100%"
                                        placeholder="Planlanan Sevk Tarihi Seçebilirsiniz"
                                        applyValueMode='instantly'
                                        showClearButton={true}
                                    />
                                </FormGroup>

                                <FormGroup style={{ marginBottom: 0 }}>
                                    <Label style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>Kontrat Sahibi</Label>
                                    <SelectBox
                                        id='kontrat-select'
                                        ref={kontratSahibiRef}
                                        placeholder="Kontrat Sahibi Seçiniz"
                                        showClearButton={true}
                                        dataSource={kontratSahibi}
                                        displayExpr="name"
                                        valueExpr="id"
                                        onValueChange={handleContractorChange}
                                        searchEnabled={true}
                                    />
                                </FormGroup>

                                <FormGroup style={{ marginBottom: 0 }}>
                                    <Label style={{ fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '500' }}>Taşeron Firma</Label>
                                    <SelectBox
                                        id='sub-kontrat-select'
                                        ref={taseronFirmaRef}
                                        placeholder="Taşeron Firma Seçiniz"
                                        showClearButton={true}
                                        dataSource={taseronFirma}
                                        displayExpr="name"
                                        valueExpr="id"
                                        onValueChange={handleSubContractorChange}
                                        searchEnabled={true}
                                    />
                                </FormGroup>

                                <FormGroup style={{ marginBottom: 0, display: "flex", flexDirection: "row", gap: "0.5rem", alignItems: "flex-end", justifyContent: "flex-start" }}>
                                    <Button
                                        id="btn-kaydet"
                                        color="danger"
                                        onClick={() => {
                                            if (validationCheck()) handlePopupVisibility('bulk');
                                        }}
                                        style={{ minWidth: "40px", padding: "0.375rem 0.5rem", flexShrink: 0 }}
                                    >
                                        <i className="fa fa-save"></i>
                                    </Button>
                                    <UncontrolledTooltip placement="top" target="btn-kaydet">
                                        Kaydet
                                    </UncontrolledTooltip>

                                    <Button
                                        id="btn-tekli-grupaj"
                                        color="warning"
                                        onClick={() => {
                                            if (validationCheck()) handlePopupVisibility('individual');
                                        }}
                                        style={{ minWidth: "40px", padding: "0.375rem 0.5rem", flexShrink: 0 }}
                                    >
                                        <i className="fa fa-plus"></i>
                                    </Button>
                                    <UncontrolledTooltip placement="top" target="btn-tekli-grupaj">
                                        Tekli Grupaj
                                    </UncontrolledTooltip>
                                </FormGroup>
                            </div>

                            {/* Selected Items Grid */}
                            <DataGrid
                                className="mt-1 data-grid-gray"
                                dataSource={selectedData}
                                showBorders={true}
                                onSelectionChanged={handleSelectedGridSelection}
                                hoverStateEnabled={true}
                                showColumnLines={true}
                                showRowLines={true}
                                height={selectedData.length > 8 ? '360' : 'auto'}
                                style={{
                                    minWidth: "100%",

                                }}
                                onCellPrepared={onCellPrepared}
                                rowAlternationEnabled={true}
                            >
                                <StateStoring
                                    enabled={true}
                                    type="localStorage"
                                    savingTimeout={100}
                                />
                                <LoadPanel enabled={false} />
                                <Selection mode="multiple" showCheckBoxesMode="none" />
                                {/*  <FilterRow
                                visible={true}
                                    operationDescriptions={operationDescriptions}
                                /> */}
                                <Scrolling
                                    mode="virtual"
                                    rowRenderingMode="virtual"
                                    // useNative={true}
                                    showScrollbar='always'
                                    preloadEnabled={true}
                                />

                                <Column
                                    alignment='center'
                                    caption='Gün'
                                    dataField='KalanGun'
                                    width={60}
                                    calculateDisplayValue={(e) => {
                                        return String(parseInt(moment().diff(e.process_date, 'days')) * -1)
                                    }}
                                    calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                                        return [
                                            [(data) => {
                                                const displayValue = String(parseInt(moment().diff(data.process_date, 'days')) * -1);
                                                return displayValue.toLowerCase().includes(filterValue.toLowerCase());
                                            }]
                                        ];
                                    }}
                                />
                                <Column
                                    alignment='center'
                                    dataField="process_date"
                                    caption="FaturaTarih"
                                    dataType="date"
                                    width={135}
                                    format="dd/MM/yyyy"
                                />
                                <Column
                                    alignment='center'
                                    caption='Öncelik'
                                    dataField='priority'
                                    width={70}
                                />
                                <Column
                                    alignment='center'
                                    dataField="chassis_no"
                                    caption="Şase"
                                    width={170}
                                    cellRender={(data) => {
                                        const safeId = data.data.chassis_no?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown';
                                        const tooltipId = `chassis-tooltip-${safeId}`;
                                        const loadingDock = formatLoadingDock(data.data.loading_dock);
                                        const isFirstSelected = data.data.chassis_no === firstSelectedChassisNo;
                                        return (
                                            <>
                                                <span id={tooltipId} style={{ cursor: loadingDock ? 'pointer' : 'default' }}>
                                                    {data.value}
                                                </span>
                                                {loadingDock && (
                                                    <Tooltip
                                                        target={`#${tooltipId}`}
                                                        showEvent="mouseenter"
                                                        hideEvent="mouseleave"
                                                        position={{
                                                            of: `#${tooltipId}`,
                                                            my: 'bottom',
                                                            at: 'top'
                                                        }}
                                                    >
                                                        <div style={{ padding: "5px" }}>
                                                            <strong>Peron:</strong> {loadingDock}
                                                            {isFirstSelected && firstSelectedPeron && (
                                                                <div style={{ marginTop: "5px", color: "#007bff" }}>
                                                                    <strong>Seçili peron:</strong> {firstSelectedPeron}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </>
                                        );
                                    }}
                                />
                                <Column
                                    alignment='center'
                                    caption='Model'
                                    dataField='model'
                                    width="95"
                                />
                                <Column
                                    alignment='center'
                                    dataField="vehicle_type"
                                    caption="Tip"
                                    width={110}
                                    calculateDisplayValue={(e) => t(e.vehicle_type)}
                                >
                                    <Lookup dataSource={[
                                        { value: "PASSENGER", text: "Binek" },
                                        { value: "COMMERCIAL", text: "Ticari" },
                                        { value: "HEAVY_COMMERCIAL", text: "Ağır Ticari" },
                                    ]} valueExpr="value" displayExpr="text" />
                                </Column>
                                <Column
                                    alignment='center'
                                    dataField="warehouse_name"
                                    caption="Nereden"
                                    minWidth={120}
                                />
                                <Column
                                    alignment='center'
                                    dataField="shipping_info"
                                    caption="Nereye"
                                    minWidth={130}

                                    cssClass='datagrid-column-whiteSpace'
                                />
                                <Column
                                    alignment='center'
                                    caption='Blg. Trh'
                                    dataField='blg_date'
                                    dataType='date'
                                    width={130}
                                    format="dd/MM/yyyy"
                                />

                                <Summary>
                                    <TotalItem column="chassis_no" summaryType="count" />
                                </Summary>
                            </DataGrid>
                        </div>
                    </Col>
                </Row>
            </div >

            <GroupageConfirmationPopup
                visible={popupVisible}
                onHiding={() => setPopupVisible(false)}
                onConfirm={handleConfirm}
                mode={groupageMode}
                selectedData={selectedData}
                route={selectedRoute}
                platform={selectedPlatform}
                plannedDate={selectedDate}
                contractor={selectedContractor}
                getPeron={getPeron}
                selectedWarehouse={selectedWarehouse}
                selectedCity={selectedCity}
            />
        </>
    );
};

export default React.memo(GrupajHazirlama);